import React, { useState, useEffect, useRef } from 'react';

const DreamMode = ({ audioContext, isSoundEnabled }) => {
  const [auroras, setAuroras] = useState([]);
  const [stars, setStars] = useState([]);
  const [shootingStars, setShootingStars] = useState([]);
  const [moon, setMoon] = useState(null);
  const [showHint, setShowHint] = useState(true);
  const [lastRClickTime, setLastRClickTime] = useState(0);
  const [rClickCount, setRClickCount] = useState(0);
  const animFrameRef = useRef(null);
  const handlersRef = useRef({});

  useEffect(() => {
    const t = setTimeout(() => setShowHint(false), 6000);
    return () => clearTimeout(t);
  }, []);

  // ─── AUDIO ─────────────────────────────────────────────────────────────────
  const playAuroraSound = () => {
    if (!audioContext || !isSoundEnabled) return;
    const now = audioContext.currentTime;
    // Crystal bell tones — ethereal shimmer
    [523.25, 659.26, 783.99, 1046.5].forEach((freq, i) => {
      const osc = audioContext.createOscillator(); const gain = audioContext.createGain();
      const filter = audioContext.createBiquadFilter();
      osc.connect(filter); filter.connect(gain); gain.connect(audioContext.destination);
      filter.type = 'highshelf'; filter.frequency.value = 2800; filter.gain.value = 5;
      osc.type = 'sine'; osc.frequency.value = freq;
      const t = now + i * 0.18;
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(0.038, t + 0.07);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 2.8);
      osc.start(t); osc.stop(t + 2.9);
    });
    // Soft noise breath
    const bufSize = audioContext.sampleRate * 2;
    const buf = audioContext.createBuffer(1, bufSize, audioContext.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < bufSize; i++) d[i] = Math.random() * 2 - 1;
    const src = audioContext.createBufferSource(); src.buffer = buf;
    const filt = audioContext.createBiquadFilter();
    filt.type = 'bandpass'; filt.frequency.value = 900; filt.Q.value = 2;
    const gain = audioContext.createGain();
    gain.gain.setValueAtTime(0, now); gain.gain.linearRampToValueAtTime(0.04, now + 0.5);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 2.0);
    src.connect(filt); filt.connect(gain); gain.connect(audioContext.destination);
    src.start(now); src.stop(now + 2.1);
  };

  const playStarTwinkle = () => {
    if (!audioContext || !isSoundEnabled) return;
    const now = audioContext.currentTime;
    const freqs = [1046.5, 1318.5, 1567.98]; // C6, E6, G6
    const freq = freqs[Math.floor(Math.random() * freqs.length)];
    const osc = audioContext.createOscillator();
    const gain = audioContext.createGain();
    const filter = audioContext.createBiquadFilter();
    osc.connect(filter); filter.connect(gain); gain.connect(audioContext.destination);
    filter.type = 'lowpass'; filter.frequency.value = 3000;
    osc.type = 'sine'; osc.frequency.value = freq;
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.04, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.5);
    osc.start(now); osc.stop(now + 0.55);
  };

  const playMoonSound = () => {
    if (!audioContext || !isSoundEnabled) return;
    const now = audioContext.currentTime;
    // Dreamy rising arpeggio (minor pentatonic)
    const notes = [220, 261.63, 311.13, 349.23, 440, 523.25, 622.25];
    notes.forEach((freq, i) => {
      const osc = audioContext.createOscillator(); const gain = audioContext.createGain();
      const filter = audioContext.createBiquadFilter();
      osc.connect(filter); filter.connect(gain); gain.connect(audioContext.destination);
      filter.type = 'lowpass'; filter.frequency.value = 1200;
      osc.type = i % 2 === 0 ? 'sine' : 'triangle'; osc.frequency.value = freq;
      const t = now + i * 0.14;
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(0.055, t + 0.1);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 2.8);
      osc.start(t); osc.stop(t + 2.9);
    });
    // Sub drone
    const sub = audioContext.createOscillator(); const subG = audioContext.createGain();
    sub.connect(subG); subG.connect(audioContext.destination);
    sub.type = 'sine'; sub.frequency.value = 55;
    subG.gain.setValueAtTime(0, now); subG.gain.linearRampToValueAtTime(0.06, now + 0.5);
    subG.gain.exponentialRampToValueAtTime(0.001, now + 4.0);
    sub.start(now); sub.stop(now + 4.1);
  };

  const playShootingStarSound = () => {
    if (!audioContext || !isSoundEnabled) return;
    const now = audioContext.currentTime;
    // Ethereal glide: frequency sweeps up then fades
    const osc = audioContext.createOscillator(); const gain = audioContext.createGain();
    const filter = audioContext.createBiquadFilter();
    osc.connect(filter); filter.connect(gain); gain.connect(audioContext.destination);
    filter.type = 'lowpass'; filter.frequency.setValueAtTime(2000, now); filter.frequency.exponentialRampToValueAtTime(500, now + 1.0);
    osc.type = 'sine'; osc.frequency.setValueAtTime(880, now); osc.frequency.exponentialRampToValueAtTime(1760, now + 0.4);
    gain.gain.setValueAtTime(0, now); gain.gain.linearRampToValueAtTime(0.08, now + 0.05);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 1.2);
    osc.start(now); osc.stop(now + 1.3);
    // Sparkle trail
    [0.1, 0.25, 0.4].forEach((delay, i) => {
      const s = audioContext.createOscillator(); const sg = audioContext.createGain();
      s.connect(sg); sg.connect(audioContext.destination);
      s.type = 'sine'; s.frequency.value = 1320 + i * 200;
      sg.gain.setValueAtTime(0, now + delay); sg.gain.linearRampToValueAtTime(0.025, now + delay + 0.04);
      sg.gain.exponentialRampToValueAtTime(0.001, now + delay + 0.35);
      s.start(now + delay); s.stop(now + delay + 0.4);
    });
  };

  // ─── ANIMATION LOOP ────────────────────────────────────────────────────────
  useEffect(() => {
    const tick = () => {
      setAuroras(prev => prev.map(a => {
        const age = Date.now() - a.born;
        return { ...a, phase: age / 850, opacity: Math.max(0, 1 - age / 7000) };
      }).filter(a => a.opacity > 0));

      setStars(prev => prev.map(s => {
        const age = Date.now() - s.born;
        const twinkle = 0.5 + 0.5 * Math.sin(age / s.twinkleSpeed);
        const opacity = s.fadingOut ? Math.max(0, 1 - (age - s.lifetime * 0.7) / (s.lifetime * 0.3)) * twinkle : twinkle;
        return { ...s, x: s.x + (s.vx || 0), y: s.y + (s.vy || 0), opacity };
      }).filter(s => Date.now() - s.born < s.lifetime));

      setShootingStars(prev => prev.map(ss => {
        const age = Date.now() - ss.born;
        const progress = age / ss.duration;
        return {
          ...ss,
          progress,
          cx: ss.startX + (ss.endX - ss.startX) * progress,
          cy: ss.startY + (ss.endY - ss.startY) * progress,
          opacity: progress < 0.1 ? progress * 10 : Math.max(0, 1 - (progress - 0.1) / 0.9)
        };
      }).filter(ss => Date.now() - ss.born < ss.duration));

      animFrameRef.current = requestAnimationFrame(tick);
    };
    animFrameRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animFrameRef.current);
  }, []);

  // ─── INTERACTION ──────────────────────────────────────────────────────────
  handlersRef.current = {
    handleMouseDown: (e) => {
      if (e.button === 0) {
        // Star twinkle cluster
        const count = 4 + Math.floor(Math.random() * 4);
        setStars(prev => [...prev, ...Array.from({ length: count }).map((_, i) => ({
          id: Date.now() + i,
          x: e.clientX + (Math.random() - 0.5) * 80,
          y: e.clientY + (Math.random() - 0.5) * 60,
          size: 3 + Math.random() * 8,
          points: 4 + Math.floor(Math.random() * 3),
            hue: 180 + Math.random() * 140,
            vx: (Math.random() - 0.5) * 0.14,
            vy: (Math.random() - 0.5) * 0.1 - 0.04,
            opacity: 1, born: Date.now(),
            lifetime: 5000 + Math.random() * 5000,
            fadingOut: true,
            twinkleSpeed: 180 + Math.random() * 280
        }))]);
        setShowHint(false);
        playStarTwinkle();
      }
      if (e.button === 2) {
        const now = Date.now();
        const tSince = now - lastRClickTime;
        if (tSince < 350 && rClickCount === 1) {
          // Double right click: shooting star
          const angle = -35 + (Math.random() - 0.5) * 20;
          const rad = angle * Math.PI / 180;
          const trailLen = 300 + Math.random() * 200;
          const id = Date.now();
          setShootingStars(prev => [...prev, {
            id, startX: e.clientX, startY: e.clientY,
            endX: e.clientX + Math.cos(rad) * trailLen,
            endY: e.clientY + Math.sin(rad) * trailLen,
            cx: e.clientX, cy: e.clientY,
            duration: 1200, born: Date.now(), progress: 0, opacity: 0
          }]);
          playShootingStarSound();
          setRClickCount(0);
        } else {
          setRClickCount(1);
          setTimeout(() => setRClickCount(0), 350);
          // Single right click: aurora wisp
          setAuroras(prev => [...prev, {
            id: Date.now() + Math.random(),
            x: e.clientX,
            y: e.clientY,
            hue: 150 + Math.random() * 130,
            width: 100 + Math.random() * 120,
            amp: 14 + Math.random() * 12,
            phase: 0,
            born: Date.now()
          }]);
          playAuroraSound();
        }
        setLastRClickTime(now);
        setShowHint(false);
      }
      if (e.button === 1) {
        e.preventDefault();
        // Moon appears at center
        setMoon({ x: e.clientX, y: e.clientY, born: Date.now() });
        playMoonSound();
        setTimeout(() => setMoon(null), 6000);
      }
    }
  };

  useEffect(() => {
    const down = (e) => handlersRef.current.handleMouseDown(e);
    const ctx = (e) => e.preventDefault();
    window.addEventListener('mousedown', down);
    window.addEventListener('contextmenu', ctx);
    return () => { window.removeEventListener('mousedown', down); window.removeEventListener('contextmenu', ctx); };
  }, []);

  const starPath = (cx, cy, pts, outer, inner) => {
    let d = '';
    for (let i = 0; i < pts * 2; i++) {
      const r = i % 2 === 0 ? outer : inner;
      const a = (Math.PI / pts) * i - Math.PI / 2;
      d += `${i === 0 ? 'M' : 'L'}${cx + Math.cos(a) * r},${cy + Math.sin(a) * r}`;
    }
    return d + 'Z';
  };

  return (
    <div style={{ position: 'fixed', inset: 0, overflow: 'hidden', zIndex: 99990, pointerEvents: 'none' }}>
      <style>{`
        @keyframes hintFadeIn { 0%{opacity:0;transform:translateY(20px)} 100%{opacity:1;transform:translateY(0)} }
        @keyframes moonGlow { 0%,100%{filter:drop-shadow(0 0 20px rgba(200,220,255,0.4))} 50%{filter:drop-shadow(0 0 45px rgba(200,220,255,0.8))} }
        @keyframes moonFadeIn { 0%{opacity:0;transform:scale(0.5)} 100%{opacity:1;transform:scale(1)} }
      `}</style>

      <svg style={{ position: 'absolute', width: '100%', height: '100%', overflow: 'visible', pointerEvents: 'none' }}>
        <defs>
          <filter id="dreamGlow">
            <feGaussianBlur stdDeviation="3" result="blur"/>
            <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
          <filter id="softBlur"><feGaussianBlur stdDeviation="2"/></filter>
        </defs>

        {/* Aurora wisps */}
        {auroras.map(a => {
          const colors = [
            `hsla(${a.hue}, 90%, 75%, 0.55)`,
            `hsla(${a.hue + 35}, 85%, 70%, 0.42)`,
            `hsla(${a.hue - 25}, 80%, 80%, 0.32)`,
          ];
          const yBase = [-a.amp * 1.8, 0, a.amp * 1.6];
          return (
            <g key={a.id} opacity={a.opacity}>
              {colors.map((color, ri) => {
                const pts = [];
                for (let j = 0; j <= 26; j++) {
                  const t = j / 26;
                  const fade = Math.sin(t * Math.PI);
                  pts.push(`${(a.x - a.width + t * a.width * 2).toFixed(1)},${(a.y + yBase[ri] + Math.sin(t * Math.PI * 2.5 + a.phase + ri * 1.3) * a.amp * fade).toFixed(1)}`);
                }
                return (
                  <polyline key={ri} points={pts.join(' ')}
                    fill="none" stroke={color}
                    strokeWidth={9 - ri * 2}
                    strokeLinecap="round" strokeLinejoin="round"
                    style={{ filter: 'blur(3px)' }} />
                );
              })}
            </g>
          );
        })}

        {/* Stars */}
        {stars.map(s => (
          <g key={s.id}>
            <path d={starPath(s.x, s.y, s.points, s.size, s.size * 0.38)}
              fill={`hsla(${s.hue}, 85%, 88%, ${s.opacity})`}
              filter="url(#dreamGlow)" />
            <circle cx={s.x} cy={s.y} r={s.size * 0.28}
              fill={`hsla(${s.hue}, 60%, 96%, ${s.opacity * 0.55})`} />
          </g>
        ))}

        {/* Shooting stars */}
        {shootingStars.map(ss => {
          const dx = ss.endX - ss.startX;
          const dy = ss.endY - ss.startY;
          const len = Math.sqrt(dx*dx+dy*dy);
          const nx = dx/len; const ny = dy/len;
          return (
            <g key={ss.id} opacity={ss.opacity}>
              {/* Trail */}
              <line x1={ss.cx} y1={ss.cy}
                x2={ss.cx - nx * Math.min(ss.progress * len, 120)}
                y2={ss.cy - ny * Math.min(ss.progress * len, 120)}
                stroke="rgba(220,240,255,0.7)" strokeWidth="2.5" strokeLinecap="round"
                style={{ filter: 'blur(1px)' }} />
              {/* Head */}
              <circle cx={ss.cx} cy={ss.cy} r="4" fill="white" filter="url(#dreamGlow)" />
              {/* Sparkles along trail */}
              {[0.3, 0.6].map((frac, i) => (
                <circle key={i}
                  cx={ss.cx - nx * frac * Math.min(ss.progress * len, 120)}
                  cy={ss.cy - ny * frac * Math.min(ss.progress * len, 120)}
                  r={2 - i} fill="rgba(200,230,255,0.8)" />
              ))}
            </g>
          );
        })}

        {/* Moon */}
        {moon && (() => {
          const age = Date.now() - moon.born;
          const opacity = Math.min(1, age < 500 ? age/500 : Math.max(0, 1 - (age - 4500)/1500));
          return (
            <g transform={`translate(${moon.x},${moon.y})`} opacity={opacity}
               style={{ animation: 'moonGlow 3s ease-in-out infinite' }}>
              {/* Moon body */}
              <circle r="45" fill="rgba(220,230,255,0.15)" stroke="rgba(200,220,255,0.5)" strokeWidth="1.5" />
              <circle r="42" fill="rgba(240,245,255,0.25)" />
              {/* Crescent shadow */}
              <circle r="38" cx="14" cy="-8" fill="rgba(20,20,60,0.45)" />
              {/* Glow rings */}
              {[60, 80, 110].map((r, i) => (
                <circle key={i} r={r} fill="none" stroke={`rgba(180,200,255,${0.12 - i*0.03})`} strokeWidth={4 - i} />
              ))}
              {/* Stars halo */}
              {Array.from({ length: 8 }).map((_, i) => {
                const a = (i / 8) * Math.PI * 2;
                const d = 70 + Math.sin(age / 500 + i) * 10;
                return <circle key={i} cx={Math.cos(a)*d} cy={Math.sin(a)*d} r="2" fill="rgba(200,220,255,0.6)" />;
              })}
            </g>
          );
        })()}
      </svg>

      {showHint && (
        <div style={{
          position: 'fixed', bottom: '160px', left: '50%', transform: 'translateX(-50%)',
          background: 'linear-gradient(135deg, rgba(20,15,60,0.92), rgba(40,30,100,0.92))',
          backdropFilter: 'blur(16px)', borderRadius: '16px', padding: '14px 22px',
          border: '1px solid rgba(150,130,255,0.3)',
          color: 'rgba(210,200,255,0.95)', fontSize: '13px',
          animation: 'hintFadeIn 0.4s ease', pointerEvents: 'none', whiteSpace: 'nowrap', zIndex: 99999
        }}>
          🌌 ЛКМ — звёздочки · ПКМ — аврора · ПКМ×2 — падающая звезда · Wheel — луна
        </div>
      )}
    </div>
  );
};

export default DreamMode;
